import React, { useState, useEffect } from 'react';

const DashboardOverview = ({ adminName, stats }) => {
  const [animateStats, setAnimateStats] = useState(false);

  useEffect(() => {
    setAnimateStats(true);
  }, []);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Modern Welcome Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl shadow-2xl p-8 text-white">
        {/* Animated Background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mt-48 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -ml-32 -mb-32 animate-pulse delay-150"></div>
        
        <div className="relative z-10">
          <h2 className="text-3xl font-extrabold mb-2 drop-shadow-lg">
            Welcome back, {adminName}! 👋
          </h2>
          <p className="text-white/90 text-base max-w-2xl">
            Manage your exams, students, and track performance from your dashboard.
          </p>
        </div>
      </div>

      {/* Main Stats Cards - Clean & Modern */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Exams */}
        <div className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 border-t-4 border-indigo-500 transition-all duration-300 hover:-translate-y-1 ${animateStats ? 'animate-slideUp' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
              <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Exams</p>
          <p className="text-4xl font-extrabold text-gray-900 mt-2 group-hover:scale-105 transition-transform duration-300">
            {stats.totalExams}
          </p>
          <div className="flex items-center gap-1 mt-3">
            <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span className="text-xs font-semibold text-green-600">All time</span>
          </div>
        </div>

        {/* Total Students */}
        <div className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 border-t-4 border-purple-500 transition-all duration-300 hover:-translate-y-1 ${animateStats ? 'animate-slideUp delay-75' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
              <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Students</p>
          <p className="text-4xl font-extrabold text-gray-900 mt-2 group-hover:scale-105 transition-transform duration-300">
            {stats.totalStudents}
          </p>
          <div className="flex items-center gap-1 mt-3">
            <svg className="h-4 w-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            <span className="text-xs font-semibold text-purple-600">Registered</span>
          </div>
        </div>

        {/* Active Exams */}
        <div className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 border-t-4 border-green-500 transition-all duration-300 hover:-translate-y-1 ${animateStats ? 'animate-slideUp delay-150' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-br from-green-100 to-green-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Active Exams</p>
          <p className="text-4xl font-extrabold text-gray-900 mt-2 group-hover:scale-105 transition-transform duration-300">
            {stats.activeExams}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <span className="text-xs font-semibold text-green-600">Live now</span>
          </div>
        </div>

        {/* Total Questions */}
        <div className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 border-t-4 border-orange-500 transition-all duration-300 hover:-translate-y-1 ${animateStats ? 'animate-slideUp delay-200' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-br from-orange-100 to-orange-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
              <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Questions</p>
          <p className="text-4xl font-extrabold text-gray-900 mt-2 group-hover:scale-105 transition-transform duration-300">
            {stats.totalQuestions}
          </p>
          <div className="flex items-center gap-1 mt-3">
            <svg className="h-4 w-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <span className="text-xs font-semibold text-orange-600">In library</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Quick Actions</h3>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Start here</span>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <button className="group relative flex flex-col items-center gap-4 p-6 border-2 border-indigo-200 rounded-xl hover:border-indigo-400 hover:bg-gradient-to-br hover:from-indigo-50 hover:to-purple-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-bold text-gray-900 text-lg">Create Exam</p>
              <p className="text-sm text-gray-600 mt-1">Set up a new exam</p>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>

          <button className="group relative flex flex-col items-center gap-4 p-6 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-bold text-gray-900 text-lg">Add Student</p>
              <p className="text-sm text-gray-600 mt-1">Register new student</p>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>

          <button className="group relative flex flex-col items-center gap-4 p-6 border-2 border-green-200 rounded-xl hover:border-green-400 hover:bg-gradient-to-br hover:from-green-50 hover:to-emerald-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-bold text-gray-900 text-lg">View Reports</p>
              <p className="text-sm text-gray-600 mt-1">Check analytics</p>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* Two Column Layout for Charts & Activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Activity - Takes 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Recent Activity</h3>
            <button className="text-sm text-indigo-600 font-semibold hover:text-indigo-700 transition">View All →</button>
          </div>
          
          {stats.totalExams === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600 font-semibold">No recent activity</p>
              <p className="text-gray-500 text-sm mt-2">Start by creating an exam or adding students</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 hover:shadow-md transition">
                <div className="bg-indigo-500 p-2.5 rounded-lg flex-shrink-0">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Exams Created</p>
                  <p className="text-xs text-gray-600 mt-1">{stats.totalExams} exam{stats.totalExams !== 1 ? 's' : ''} added to your organization</p>
                </div>
                <span className="text-xs text-gray-500 flex-shrink-0">Today</span>
              </div>

              {stats.activeExams > 0 && (
                <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-md transition">
                  <div className="bg-green-500 p-2.5 rounded-lg flex-shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Active Exams Running</p>
                    <p className="text-xs text-gray-600 mt-1">{stats.activeExams} exam{stats.activeExams !== 1 ? 's' : ''} currently available for students</p>
                  </div>
                  <span className="relative flex h-2 w-2 mt-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                </div>
              )}

              {stats.totalQuestions > 0 && (
                <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100 hover:shadow-md transition">
                  <div className="bg-orange-500 p-2.5 rounded-lg flex-shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Question Library Updated</p>
                    <p className="text-xs text-gray-600 mt-1">{stats.totalQuestions} question{stats.totalQuestions !== 1 ? 's' : ''} available in your bank</p>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">Recent</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Stats Summary - 1 column */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Exams</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{stats.totalExams}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Students</span>
              </div>
              <span className="text-lg font-bold text-purple-600">{stats.totalStudents}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">Active Now</span>
              </div>
              <span className="text-lg font-bold text-green-600">{stats.activeExams}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Questions</span>
              </div>
              <span className="text-lg font-bold text-orange-600">{stats.totalQuestions}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
